import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "@/index.css";
import App from "@/App";

// Suppress benign ResizeObserver loop notifications that surface as CRA's
// red error overlay when Radix Selects open inside Dialogs. The error is
// non-blocking, doesn't break flows, and is a known Radix/CRA-overlay issue.
if (typeof window !== "undefined") {
  const RO_MATCH = /ResizeObserver loop (limit exceeded|completed)/;
  const matches = (val) => typeof val === "string" && RO_MATCH.test(val);
  const swallow = (e) => {
    const msg = matches(e?.message) ? e.message
      : matches(e?.reason?.message) ? e.reason.message
      : matches(e?.error?.message) ? e.error.message
      : "";
    if (msg) {
      e.preventDefault?.();
      e.stopImmediatePropagation?.();
      return false;
    }
    return undefined;
  };
  window.addEventListener("error", swallow, true);
  window.addEventListener("unhandledrejection", swallow, true);
  // CRA's dev overlay listens on window.onerror — patch console.error too.
  const origConsoleError = console.error;
  console.error = (...args) => {
    if (args.some((a) => matches(a?.message) || matches(a))) return;
    origConsoleError.apply(console, args);
  };
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  },
});

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </React.StrictMode>,
);

// PWA: register service worker (app-shell only caching).
// Disabled on localhost/dev to avoid cache surprises during development.
if ("serviceWorker" in navigator && window.location.hostname !== "localhost") {
  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/service-worker.js")
      .then((reg) => {
        // Detect a newer SW waiting to activate
        const promptUpdate = (waiting) => {
          // Lazy-load sonner; only run after the toaster is mounted by React.
          import("sonner").then(({ toast }) => {
            toast("New version available", {
              description: "Click to update for the latest features and fixes.",
              duration: 60000,
              action: {
                label: "Update",
                onClick: () => {
                  waiting.postMessage("SKIP_WAITING");
                  // The SW will call skipWaiting; reload once it takes control.
                  navigator.serviceWorker.addEventListener(
                    "controllerchange",
                    () => window.location.reload(),
                    { once: true },
                  );
                },
              },
            });
          }).catch(() => { /* sonner not yet loaded — silent skip */ });
        };
        if (reg.waiting) promptUpdate(reg.waiting);
        reg.addEventListener("updatefound", () => {
          const newWorker = reg.installing;
          if (!newWorker) return;
          newWorker.addEventListener("statechange", () => {
            if (newWorker.state === "installed" && navigator.serviceWorker.controller) {
              promptUpdate(newWorker);
            }
          });
        });
      })
      .catch(() => { /* ignore registration errors — SW is non-essential */ });
  });
}
